import { useCallback, useEffect, useMemo, useState } from "react";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCw, Info } from "lucide-react";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

type SyncState = {
  currency: string | null; timezone: string | null; status: string; last_attempt_at: string | null;
  last_success_at: string | null; last_error: string | null; backfill_done: boolean;
  oldest_order_at: string | null; order_count: number; customer_data_restricted: boolean; granted_scopes: string[] | null;
};
type Customer = { id: string; name: string | null; email: string | null; tags: string[] };
type Order = { id: string; name: string; customer_id: string | null; created_at: string; processed_at: string | null; cancelled_at: string | null; test: boolean; financial_status: string | null; total: number; subtotal: number; tax: number; shipping: number };
type Line = { id: string; order_id: string; title: string; quantity: number; refunded_quantity: number; discounted_total: number; refunded_amount: number };
type Tx = { id: string; order_id: string; kind: string; status: string; amount: number; processed_at: string | null; test: boolean };

const DAY = 86_400_000;

// Existing programme hierarchy (highest first).
const TIERS: [string, RegExp][] = [
  ["Stage 2 – Investor Engagement", /stage 2|investor engagement/i],
  ["Embedded Advisor", /embedded advisor/i],
  ["Stage 1 – Investor Readiness", /stage 1|investor readiness/i],
  ["Founding Team Sprint", /founding team sprint|founder consulting/i],
  ["Advisory tokens", /token/i],
  ["Courses & workshops", /course|workshop|masterclass/i],
  ["Book", /founder laws/i],
];
const tierOf = (titles: string[], tags: string[]) => {
  const tag = tags.find((t) => /^tier\s*[:\-]/i.test(t));
  if (tag) return tag.replace(/^tier\s*[:\-]\s*/i, "");
  for (const [name, re] of TIERS) if (titles.some((t) => re.test(t))) return name;
  return "Unclassified";
};

async function fetchAll<T>(table: string, cols: string): Promise<T[]> {
  const out: T[] = [];
  for (let from = 0; ; from += 1000) {
    const { data, error } = await db.from(table).select(cols).range(from, from + 999);
    if (error) throw error;
    out.push(...data);
    if (data.length < 1000) return out;
  }
}

const median = (xs: number[]) => { const s = [...xs].sort((a, b) => a - b); const m = s.length >> 1; return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; };

export const ShopifyFinance = () => {
  const [state, setState] = useState<SyncState | null>(null);
  const [data, setData] = useState<{ customers: Customer[]; orders: Order[]; lines: Line[]; txs: Tx[] } | null>(null);
  const [loadError, setLoadError] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");
  const [month, setMonth] = useState("");

  const load = useCallback(async () => {
    setLoadError("");
    try {
      const { data: st, error } = await db.from("shop_sync_state").select("*").eq("id", 1).maybeSingle();
      if (error) throw error;
      setState(st);
      const [customers, orders, lines, txs] = await Promise.all([
        fetchAll<Customer>("shop_customers", "id,name,email,tags"),
        fetchAll<Order>("shop_orders", "id,name,customer_id,created_at,processed_at,cancelled_at,test,financial_status,total,subtotal,tax,shipping"),
        fetchAll<Line>("shop_line_items", "id,order_id,title,quantity,refunded_quantity,discounted_total,refunded_amount"),
        fetchAll<Tx>("shop_transactions", "id,order_id,kind,status,amount,processed_at,test"),
      ]);
      setData({ customers, orders, lines, txs });
    } catch {
      setLoadError("Saved Shopify figures could not be loaded. Please reload.");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setSyncing(true); setSyncMsg("");
    try {
      for (let i = 0; i < 30; i++) {
        const { data: r, error } = await supabase.functions.invoke("shopify-finance", { body: { action: "sync" } });
        if (error) {
          let msg = error.message;
          if (error instanceof FunctionsHttpError) { try { msg = (await error.context.json()).error ?? msg; } catch { /* keep */ } }
          setSyncMsg(msg); break;
        }
        setSyncMsg(r.more ? `Importing order history… ${r.orders} orders so far` : "");
        if (!r.more) break;
      }
    } finally { setSyncing(false); await load(); }
  };

  const tz = state?.timezone ?? "Pacific/Auckland";
  const currency = state?.currency ?? "NZD";
  const monthKey = useCallback((iso: string) => {
    const p = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit" }).formatToParts(new Date(iso));
    return `${p.find((x) => x.type === "year")!.value}-${p.find((x) => x.type === "month")!.value}`;
  }, [tz]);
  const nowMonth = monthKey(new Date().toISOString());
  const selected = month || nowMonth;
  const money = (v: number) => new Intl.NumberFormat("en-NZ", { style: "currency", currency, currencyDisplay: "code" }).format(v);
  const fmtDate = (d: number | string | null) => d == null ? "—" : new Date(d).toLocaleDateString("en-NZ", { timeZone: tz, day: "numeric", month: "short", year: "numeric" });

  const r = useMemo(() => {
    if (!data) return null;
    const now = Date.now();
    const orderById = new Map(data.orders.map((o) => [o.id, o]));
    const live = (o?: Order) => !!o && !o.test;
    const custById = new Map(data.customers.map((c) => [c.id, c]));

    // Cash events (deduplicated by transaction id at storage).
    const ok = data.txs.filter((t) => t.status === "SUCCESS" && !t.test && t.processed_at && live(orderById.get(t.order_id)));
    const pay = ok.filter((t) => t.kind === "SALE" || t.kind === "CAPTURE");
    const ref = ok.filter((t) => t.kind === "REFUND");

    const months = new Map<string, { gross: number; refunds: number; orders: Set<string> }>();
    const bump = (k: string) => { let m = months.get(k); if (!m) { m = { gross: 0, refunds: 0, orders: new Set() }; months.set(k, m); } return m; };
    for (const t of pay) { const m = bump(monthKey(t.processed_at!)); m.gross += Number(t.amount); m.orders.add(t.order_id); }
    for (const t of ref) bump(monthKey(t.processed_at!)).refunds += Number(t.amount);

    // Per-client payment history, grouped by order (instalments of one order count once).
    type C = { id: string; name: string; email: string | null; tags: string[]; orderFirstPay: Map<string, number>; gross: number; refunds: number; mGross: number; mRefunds: number; titles: string[] };
    const clients = new Map<string, C>();
    const unlinked = new Set<string>();
    const cl = (id: string) => {
      let c = clients.get(id);
      if (!c) { const cu = custById.get(id); c = { id, name: cu?.name || "Name hidden by Shopify", email: cu?.email ?? null, tags: cu?.tags ?? [], orderFirstPay: new Map(), gross: 0, refunds: 0, mGross: 0, mRefunds: 0, titles: [] }; clients.set(id, c); }
      return c;
    };
    for (const t of pay) {
      const o = orderById.get(t.order_id)!; const ts = new Date(t.processed_at!).getTime();
      if (!o.customer_id) { unlinked.add(o.id); continue; }
      const c = cl(o.customer_id); c.gross += Number(t.amount);
      if (monthKey(t.processed_at!) === selected) c.mGross += Number(t.amount);
      const prev = c.orderFirstPay.get(o.id); if (prev === undefined || ts < prev) c.orderFirstPay.set(o.id, ts);
    }
    for (const t of ref) {
      const o = orderById.get(t.order_id)!; if (!o.customer_id) continue;
      const c = cl(o.customer_id); c.refunds += Number(t.amount);
      if (monthKey(t.processed_at!) === selected) c.mRefunds += Number(t.amount);
    }
    const linesByOrder = new Map<string, Line[]>();
    for (const l of data.lines) { const a = linesByOrder.get(l.order_id) ?? []; a.push(l); linesByOrder.set(l.order_id, a); }
    const orderPaid = new Map<string, number>();
    for (const t of pay) orderPaid.set(t.order_id, (orderPaid.get(t.order_id) ?? 0) + Number(t.amount));

    const rows = [...clients.values()].map((c) => {
      for (const oid of c.orderFirstPay.keys()) c.titles.push(...(linesByOrder.get(oid) ?? []).map((l) => l.title));
      const dates = [...c.orderFirstPay.values()].sort((a, b) => a - b);
      const gaps = dates.slice(1).map((d, i) => (d - dates[i]) / DAY);
      const last = dates[dates.length - 1];
      let rhythm = "One-off", interval: number | null = null;
      if (dates.length === 2) rhythm = "Insufficient history";
      else if (dates.length >= 3) {
        const med = median(gaps); const dev = Math.max(...gaps.map((g) => Math.abs(g - med)));
        if (med >= 20 && med <= 40 && dev <= 12) { rhythm = "Monthly"; interval = med; }
        else if (med >= 75 && med <= 105 && dev <= 25) { rhythm = "Quarterly"; interval = med; }
        else rhythm = "Irregular";
      }
      const next = interval ? last + interval * DAY : null;
      const missed = next !== null && now > next + 7 * DAY;
      const stopped = next !== null && now > next + interval! * DAY; // a full cycle past expected
      const typical = median([...c.orderFirstPay.keys()].map((o) => orderPaid.get(o) ?? 0));
      return { ...c, count: dates.length, last, rhythm, interval, next, missed, stopped, typical, tier: tierOf(c.titles, c.tags), net: c.gross - c.refunds, mNet: c.mGross - c.mRefunds, daysSince: Math.floor((now - last) / DAY) };
    }).sort((a, b) => b.last - a.last);

    const recurring = rows.filter((c) => (c.rhythm === "Monthly" || c.rhythm === "Quarterly") && !c.stopped);
    const mrr = recurring.reduce((a, c) => a + (c.rhythm === "Monthly" ? c.typical : c.typical / 3), 0);
    const m = months.get(selected) ?? { gross: 0, refunds: 0, orders: new Set<string>() };
    const paidInMonth = rows.filter((c) => c.mGross > 0 || c.mRefunds > 0);

    // Products & services: discounted line totals less line-item refunds, excl. tax & shipping, by order date.
    const products = new Map<string, { qty: number; sales: number; mQty: number; mSales: number }>();
    for (const l of data.lines) {
      const o = orderById.get(l.order_id); if (!live(o) || o!.cancelled_at && !orderPaid.has(o!.id)) continue;
      const p = products.get(l.title) ?? { qty: 0, sales: 0, mQty: 0, mSales: 0 };
      const q = l.quantity - l.refunded_quantity, s = Number(l.discounted_total) - Number(l.refunded_amount);
      p.qty += q; p.sales += s;
      if (monthKey(o!.created_at) === selected) { p.mQty += q; p.mSales += s; }
      products.set(l.title, p);
    }

    const tiers = new Map<string, number>();
    for (const c of rows.filter((c) => now - c.last <= 365 * DAY)) tiers.set(c.tier, (tiers.get(c.tier) ?? 0) + 1);

    const last12: string[] = [];
    { const [y, mo] = nowMonth.split("-").map(Number); for (let i = 0; i < 12; i++) { const d = new Date(Date.UTC(y, mo - 1 - i, 1)); last12.push(d.toISOString().slice(0, 7)); } }
    const allMonths = [...new Set([nowMonth, ...months.keys()])].sort().reverse();

    return {
      summary: { gross: m.gross, refunds: m.refunds, net: m.gross - m.refunds, orders: m.orders.size, aov: m.orders.size ? m.gross / m.orders.size : 0, clients: paidInMonth.length, mrr },
      paidInMonth, rows, recurring,
      history: last12.map((k) => { const v = months.get(k); return { k, gross: v?.gross ?? 0, refunds: v?.refunds ?? 0, orders: v?.orders.size ?? 0 }; }),
      products: [...products.entries()].sort((a, b) => b[1].sales - a[1].sales),
      tiers: [...tiers.entries()].sort((a, b) => b[1] - a[1]),
      exceptions: data.orders.filter((o) => !o.test && !o.cancelled_at && ["PENDING", "PARTIALLY_PAID", "AUTHORIZED", "UNPAID", "EXPIRED"].includes(o.financial_status ?? "")),
      stopped: rows.filter((c) => c.missed),
      unlinked: [...unlinked].map((id) => orderById.get(id)!),
      allMonths,
    };
  }, [data, selected, monthKey, nowMonth]);

  const stale = state?.status === "error" && !!state.last_success_at;
  const never = !state?.last_success_at;
  const isMTD = selected === nowMonth;

  return (
    <section className="space-y-6 mb-10" aria-label="Clients and revenue from Shopify">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h2 className="font-sans text-xl font-bold">Clients & revenue</h2>
          <p className="text-sm text-muted-foreground font-sans">
            Shopify · {state?.status === "ok" ? "Connected" : state?.status === "error" ? "Connection problem" : state?.status === "partial" ? "Import in progress" : "Not yet synced"}
            {state?.last_success_at && ` · last successful sync ${new Date(state.last_success_at).toLocaleString("en-NZ", { timeZone: tz })}`}
            {state?.oldest_order_at && ` · history from ${fmtDate(state.oldest_order_at)} (${state.order_count} orders${state.backfill_done ? "" : ", import incomplete"})`}
            {state?.currency && ` · ${currency}, ${tz}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select aria-label="Month" className="h-9 rounded-md border border-input bg-background px-2 text-sm font-sans" value={selected} onChange={(e) => setMonth(e.target.value)}>
            {(r?.allMonths ?? [nowMonth]).map((k) => <option key={k} value={k}>{k}{k === nowMonth ? " (month to date)" : ""}</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={refresh} disabled={syncing}>
            {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}Refresh data
          </Button>
        </div>
      </div>

      {syncMsg && syncMsg !== state?.last_error && <p role="status" className="text-sm font-sans border border-border rounded-md p-3">{syncMsg}</p>}
      {state?.status === "error" && state.last_error && (
        <p role="alert" className="text-sm text-destructive border border-destructive/40 rounded-md p-3 font-sans">
          {stale ? "Showing figures from the last successful sync — they may be out of date. " : ""}{state.last_error}
        </p>
      )}
      {state?.status === "ok" && state.last_error && <p className="text-sm font-sans border border-border rounded-md p-3">{state.last_error}</p>}
      {state?.customer_data_restricted && <p className="text-sm font-sans border border-border rounded-md p-3">Shopify is withholding customer names and emails (protected customer data not approved for this app). Clients are still tracked by their Shopify ID.</p>}
      {loadError && <p role="alert" className="text-sm text-destructive">{loadError}</p>}

      {never ? (
        <p className="text-sm text-muted-foreground font-sans">No Shopify data has been imported yet, so no figures are shown. Press Refresh data once the app is installed.</p>
      ) : r && (
        <>
          <div>
            <h3 className="font-sans font-semibold mb-2">{isMTD ? "Month to date" : "Month"} · {selected}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {([
                ["Gross collected", money(r.summary.gross), "Successful SALE and CAPTURE payments processed this month (store time). Authorisations, pending and failed payments excluded. Includes tax and shipping; Shopify payment fees are not deducted."],
                ["Refunds", money(r.summary.refunds), "Successful refunds processed this month, whatever month the original sale was."],
                ["Net collected", money(r.summary.net), "Gross collected minus refunds."],
                ["Paid orders", String(r.summary.orders), "Distinct orders with at least one successful payment this month."],
                ["Avg paid order", money(r.summary.aov), "Gross collected ÷ paid orders for this month."],
                ["Clients who paid", String(r.summary.clients), "Distinct Shopify customers with a payment or refund this month."],
                ["Est. monthly recurring", money(r.summary.mrr), "Estimate only: typical payment of clients with an established monthly rhythm, plus one-third for quarterly clients. One-off and irregular buyers are excluded."],
              ] as const).map(([l, v, tip]) => (
                <div key={l} className="border border-border rounded-md p-3 bg-card" title={tip}>
                  <p className="text-xs text-muted-foreground font-sans flex items-center gap-1">{l}<Info className="h-3 w-3" aria-hidden /></p>
                  <p className="font-sans text-lg font-semibold">{v}</p>
                </div>
              ))}
            </div>
          </div>

          <Block title={`Paying clients — ${selected}`}>
            <Table head={["Client", "Tier", "This month: collected", "Refunds", "Net", "Lifetime: payments", "Collected", "Refunds", "Net paid", "Last payment"]}
              rows={r.paidInMonth.map((c) => [
                <ClientCell key="c" c={c} />, c.tier, money(c.mGross), money(c.mRefunds), money(c.mNet),
                c.count, money(c.gross), money(c.refunds), money(c.net), fmtDate(c.last),
              ])} empty="No client payments in this month." />
          </Block>

          <Block title="Payment rhythm & next expected payment (estimates)">
            <Table head={["Client", "Tier", "Rhythm", "Paid orders", "Days since last", "Next expected", "Typical amount"]}
              rows={r.rows.filter((c) => c.count > 1).map((c) => [
                <ClientCell key="c" c={c} />, c.tier, c.rhythm + (c.interval ? ` (~${Math.round(c.interval)} days)` : ""), c.count, c.daysSince,
                c.next ? <span key="n" className={c.missed ? "text-destructive font-medium" : ""}>{fmtDate(c.next)}{c.missed ? " · Expected payment missed" : ""}</span> : "—",
                money(c.typical),
              ])} empty="No repeat clients yet." />
            <p className="text-xs text-muted-foreground font-sans">A rhythm needs at least three separately paid orders; instalments on one order count once. Dates are inferred, not invoice due dates — no overdue invoices are known (draft invoices need the read_draft_orders permission, which this app doesn't have).</p>
          </Block>

          <div className="grid lg:grid-cols-2 gap-6">
            <Block title="Payment history (last 12 months)">
              <Table head={["Month", "Collected", "Refunds", "Net", "Paid orders"]}
                rows={r.history.map((h) => [h.k + (h.k === nowMonth ? " (MTD)" : ""), money(h.gross), money(h.refunds), money(h.gross - h.refunds), h.orders])} empty="" />
            </Block>
            <Block title="Clients by tier (paid in last 12 months)">
              <div className="flex flex-wrap gap-2">
                {r.tiers.length ? r.tiers.map(([t, n]) => <Badge key={t} variant="outline" className="font-sans">{t}: {n}</Badge>) : <p className="text-sm text-muted-foreground">No clients.</p>}
              </div>
              <p className="text-xs text-muted-foreground font-sans">Uses a Shopify customer tag such as “tier: Gold” first, otherwise the highest programme purchased.</p>
            </Block>
          </div>

          <Block title="Products & services sold">
            <Table head={["Product / service", `Qty ${selected}`, `Sales ${selected}`, "Qty lifetime", "Sales lifetime"]}
              rows={r.products.map(([t, p]) => [t, p.mQty, money(p.mSales), p.qty, money(p.sales)])} empty="No sales yet." />
            <p className="text-xs text-muted-foreground font-sans">Sales are after discounts and line-item refunds, excluding tax and shipping, dated by order date — so they differ from cash collected, which includes tax and shipping and is dated by payment and refund processing.</p>
          </Block>

          <div className="grid lg:grid-cols-2 gap-6">
            <Block title="Exceptions: unpaid, partially paid or pending orders">
              <Table head={["Order", "Client", "Date", "Status", "Total"]}
                rows={r.exceptions.map((o) => [o.name, o.customer_id ? (data!.customers.find((c) => c.id === o.customer_id)?.name ?? "—") : "No linked customer", fmtDate(o.created_at), (o.financial_status ?? "").replace(/_/g, " ").toLowerCase(), money(Number(o.total))])}
                empty="Nothing outstanding." />
            </Block>
            <Block title="Exceptions: repeat clients whose pattern has stopped">
              <Table head={["Client", "Rhythm", "Last payment", "Expected"]} rows={r.stopped.map((c) => [<ClientCell key="c" c={c} />, c.rhythm, fmtDate(c.last), fmtDate(c.next)])} empty="None." />
            </Block>
          </div>
          {r.unlinked.length > 0 && (
            <Block title="Paid orders without a linked customer">
              <Table head={["Order", "Date", "Total"]} rows={r.unlinked.map((o) => [o.name, fmtDate(o.created_at), money(Number(o.total))])} empty="" />
              <p className="text-xs text-muted-foreground font-sans">Counted in cash totals, but not assigned to any client.</p>
            </Block>
          )}
          <p className="text-xs text-muted-foreground font-sans">Test orders are excluded. Payments and refunds on cancelled orders are kept so cash totals reconcile with Shopify.</p>
        </>
      )}
    </section>
  );
};

const ClientCell = ({ c }: { c: { name: string; email: string | null } }) => (
  <div><p className="font-medium">{c.name}</p>{c.email && <p className="text-xs text-muted-foreground">{c.email}</p>}</div>
);

const Block = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="space-y-2"><h3 className="font-sans font-semibold">{title}</h3>{children}</div>
);

const Table = ({ head, rows, empty }: { head: string[]; rows: React.ReactNode[][]; empty: string }) =>
  rows.length === 0 ? <p className="text-sm text-muted-foreground">{empty}</p> : (
    <div className="overflow-x-auto border border-border rounded-md">
      <table className="w-full text-sm font-sans">
        <thead className="bg-muted/50"><tr>{head.map((h, i) => <th key={i} className="text-left font-medium p-2 whitespace-nowrap">{h}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-border">{r.map((c, j) => <td key={j} className="p-2 align-top">{c}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
